import { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Divider, IconButton, Stack, Typography } from "@mui/material";
import {
  Download,
  Fullscreen,
  FullscreenExit,
  NavigateBefore,
  NavigateNext,
  RestartAlt,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";

import { decodeTiff, TiffPage } from "./decodeTiff";

const ZOOM_MINIMO = 0.1;
const ZOOM_MAXIMO = 4;
const ZOOM_PASSO = 0.1;
const ZOOM_PADRAO = 0.2;

interface TiffViewerProps {
  arquivo: string;
  width?: string;
  height?: string;
}

function TiffViewer({ arquivo, width = "98%", height = "800px" }: TiffViewerProps) {
  const [paginas, setPaginas] = useState<TiffPage[] | null>(null);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [zoom, setZoom] = useState(ZOOM_PADRAO);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [telaCheia, setTelaCheia] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      setPaginas(null);
      setPaginaAtual(0);
      setZoom(ZOOM_PADRAO);

      try {
        const resposta = await fetch(arquivo);
        const buffer = await resposta.arrayBuffer();
        const decodificadas = await decodeTiff(buffer);

        if (!cancelado) {
          setPaginas(decodificadas);
        }
      } catch {
        if (!cancelado) {
          setErro("Não foi possível renderizar este arquivo TIFF.");
        }
      } finally {
        if (!cancelado) {
          setCarregando(false);
        }
      }
    }

    carregar();

    return () => {
      cancelado = true;
    };
  }, [arquivo]);

  useEffect(() => {
    const pagina = paginas?.[paginaAtual];
    const canvas = canvasRef.current;

    if (!pagina || !canvas) return;

    canvas.width = pagina.width;
    canvas.height = pagina.height;
    canvas.getContext("2d")?.putImageData(pagina.imageData, 0, 0);
  }, [paginas, paginaAtual]);

  useEffect(() => {
    function aoAlterarTelaCheia() {
      setTelaCheia(document.fullscreenElement === containerRef.current);
    }

    document.addEventListener("fullscreenchange", aoAlterarTelaCheia);
    return () => document.removeEventListener("fullscreenchange", aoAlterarTelaCheia);
  }, []);

  const alternarTelaCheia = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }, []);

  const baixarArquivo = useCallback(() => {
    const link = document.createElement("a");
    link.href = arquivo;
    link.download = "documento.tif";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [arquivo]);

  if (carregando) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ mt: 4 }}>
        <CircularProgress />
        <Typography>Carregando visualização do TIFF...</Typography>
      </Stack>
    );
  }

  if (erro || !paginas) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ mt: 4 }}>
        <Typography>{erro ?? "Não foi possível carregar o arquivo TIFF."}</Typography>
        <IconButton color="primary" onClick={baixarArquivo} title="Baixar arquivo TIFF">
          <Download />
        </IconButton>
      </Stack>
    );
  }

  const totalPaginas = paginas.length;
  const paginaAtiva = paginas[paginaAtual];

  return (
    <Stack alignItems="center" spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="center">
        <IconButton size="small" disabled={paginaAtual === 0} onClick={() => setPaginaAtual((p) => p - 1)}>
          <NavigateBefore />
        </IconButton>

        <Typography variant="body2" minWidth={90} textAlign="center">
          Página {paginaAtual + 1} de {totalPaginas}
        </Typography>

        <IconButton
          size="small"
          disabled={paginaAtual === totalPaginas - 1}
          onClick={() => setPaginaAtual((p) => p + 1)}
        >
          <NavigateNext />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <IconButton
          size="small"
          disabled={zoom <= ZOOM_MINIMO}
          onClick={() => setZoom((z) => Math.max(ZOOM_MINIMO, +(z - ZOOM_PASSO).toFixed(2)))}
        >
          <ZoomOut />
        </IconButton>

        <Typography variant="body2" minWidth={48} textAlign="center">
          {Math.round(zoom * 100)}%
        </Typography>

        <IconButton
          size="small"
          disabled={zoom >= ZOOM_MAXIMO}
          onClick={() => setZoom((z) => Math.min(ZOOM_MAXIMO, +(z + ZOOM_PASSO).toFixed(2)))}
        >
          <ZoomIn />
        </IconButton>

        <IconButton size="small" onClick={() => setZoom(ZOOM_PADRAO)} title="Restaurar zoom">
          <RestartAlt />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <IconButton size="small" onClick={baixarArquivo} title="Baixar arquivo TIFF">
          <Download />
        </IconButton>

        <IconButton size="small" onClick={alternarTelaCheia} title="Tela cheia">
          {telaCheia ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
      </Stack>

      <Box
        ref={containerRef}
        tabIndex={0}
        onKeyDown={(evento) => {
          if (evento.key === "ArrowRight") setPaginaAtual((p) => Math.min(p + 1, totalPaginas - 1));
          if (evento.key === "ArrowLeft") setPaginaAtual((p) => Math.max(p - 1, 0));
        }}
        sx={{
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0,
          maxWidth: telaCheia ? "100%" : width,
          height: telaCheia ? "100%" : "auto",
          maxHeight: telaCheia ? undefined : height,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          outline: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: paginaAtiva.width * zoom,
            height: paginaAtiva.height * zoom,
          }}
        />
      </Box>
    </Stack>
  );
}

export default TiffViewer;
